import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import { formatAppArea } from "@/v2-backoffice-client/usage/usage.constants"
import {
  formatCompactAmount,
  formatInvoiceAmount,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { derivePortfolioAreaValue } from "@/v2-backoffice-client/portfolio/portfolio.area.utils"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioAreaValueProps = ComponentProps<"section"> & {
  customers: PortfolioCustomerSummary[]
}

/**
 * A capability whose share of the money differs from its share of the usage
 * by at least this many percentage points gets a read chip: above = premium
 * anchor (few big payers carry it), below = under-monetized (heavily used
 * relative to what it earns).
 */
const MISMATCH_THRESHOLD = 0.1

// "Where the money works": each paying customer's 12-month billed revenue
// spread across the areas they actually use (proportional to their weighted
// 28-day volume), stacked against each area's share of the fleet's usage.
// The gaps are the strategy read — premium anchors to protect, workhorses
// to monetize — and the red slice is the money working in an area while its
// owner drifts toward churn.
function PortfolioAreaValue(props: PortfolioAreaValueProps) {
  const { customers, className, ...sectionProps } = props

  const book = derivePortfolioAreaValue(customers)
  // One bar per row (billed value), so rows carrying neither money nor a
  // mismatch signal are noise — drop them instead of rendering dashes.
  const rows = book.rows.filter(
    (row) => row.attributed > 0 || row.usageShare >= MISMATCH_THRESHOLD,
  )
  const maxAttributed = Math.max(1, ...rows.map((row) => row.attributed))

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Money"
        title="Where the money works"
        lead="The last twelve months' billing spread across the capabilities customers actually use, with the share at churn risk in red."
        info="Each paying customer's 12-month billed revenue, spread across the capabilities they actually use in proportion to their weighted 28-day volume — an attribution model of what the money is used FOR, not invoice line items. The red slice is value attributed to customers at elevated churn risk. The chips compare money share with usage share: 'Premium anchor' = far more money than usage (few big payers carry it — protect it); 'Under-monetized' = far more usage than money (a pricing or expansion conversation). Hover a bar for the exact shares."
      >
        <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-primary" />
            billed value
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-destructive" />
            of it at churn risk
          </span>
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No tracked interest-area usage to attribute revenue to yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            <li className="usage-report-list-head gap-3" aria-hidden>
              <span className="w-44 shrink-0">Capability</span>
              <span className="min-w-0 flex-1">
                Share of billed value (red: at churn risk)
              </span>
              <span className="w-20 shrink-0 text-right">Value</span>
              <span className="w-10 shrink-0 text-right">Share</span>
              <span className="w-32 shrink-0 text-right">Read</span>
            </li>
            {rows.map((row) => {
              const gap = row.revenueShare - row.usageShare
              const barShare = row.attributed / maxAttributed
              const atRiskShare =
                row.attributed > 0 ? row.atRiskAttributed / row.attributed : 0
              return (
                <li key={row.area} className="flex items-center gap-3">
                  <span className="w-44 shrink-0 truncate text-xs">
                    {formatAppArea(row.area)}
                  </span>

                  <span
                    className="flex h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
                    title={`${formatInvoiceAmount(Math.round(row.attributed), book.currency)} attributed (${formatNumber(Math.round(row.revenueShare * 100))}% of the book) — ${row.payingAdopters} paying ${row.payingAdopters === 1 ? "customer works" : "customers work"} here · ${formatNumber(Math.round(row.usageShare * 100))}% of fleet usage${row.atRiskAttributed > 0 ? ` · ${formatCompactAmount(row.atRiskAttributed, book.currency)} of it on at-risk customers` : ""}`}
                  >
                    <span
                      className="block h-full bg-primary"
                      style={{
                        width: `${barShare * (1 - atRiskShare) * 100}%`,
                      }}
                    />
                    {atRiskShare > 0 && (
                      <span
                        className="block h-full bg-destructive"
                        style={{ width: `${barShare * atRiskShare * 100}%` }}
                      />
                    )}
                  </span>

                  <span className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {row.attributed > 0
                      ? formatCompactAmount(row.attributed, book.currency)
                      : "—"}
                  </span>

                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {row.attributed > 0
                      ? `${formatNumber(Math.round(row.revenueShare * 100))}%`
                      : ""}
                  </span>

                  <span className="flex w-32 shrink-0 justify-end">
                    {gap >= MISMATCH_THRESHOLD ? (
                      <Badge
                        size="xs"
                        variant="neutral"
                        tooltip="This capability carries far more money than usage — a premium anchor held up by few big payers. Protect these workflows and the accounts behind them; losing one hurts."
                      >
                        Premium anchor
                      </Badge>
                    ) : gap <= -MISMATCH_THRESHOLD ? (
                      <Badge
                        size="xs"
                        variant="warning"
                        tooltip="Heavily used relative to the money it earns — the fleet works here but the billing doesn't reflect it. A pricing, packaging or upsell conversation waiting to happen."
                      >
                        Under-monetized
                      </Badge>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
        )}

        {(book.attributedTotal > 0 || book.unattributed > 0) && (
          <p className="text-xs text-muted-foreground">
            {formatInvoiceAmount(
              Math.round(book.attributedTotal),
              book.currency,
            )}{" "}
            billed value anchored in tracked usage
            {book.unattributed > 0 && (
              <>
                {" · "}
                <span
                  className="text-amber-500/90"
                  title="Paying customers with no tracked interest-area usage at all — revenue with no usage footprint behind it. Until they adopt something, nothing ties this money to the product."
                >
                  {formatCompactAmount(book.unattributed, book.currency)} billed
                  with no usage footprint
                </span>
              </>
            )}
          </p>
        )}
      </UsageReportCard>
    </section>
  )
}

export default PortfolioAreaValue
